/**
 * Groups API Service
 * Handles dynamic member grouping with rule-based filters
 */

import { apiClient, handleApiError } from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface Filter {
  field: string;
  operator: 'EQ' | 'NE' | 'LT' | 'GT' | 'LE' | 'GE' | 'IN' | 'NOT_IN' | 'CONTAINS' | 'STARTS_WITH' | 'ENDS_WITH';
  value: any;
  logic_operator: 'AND' | 'OR';
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  organization_id: number;
  program_id?: number;
  member_count: number;
  freeze_timestamp?: string;
  filters: Filter[];
  created_by: number;
  created_at: string;
  last_updated_at?: string;
}

export interface GroupMember {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_number?: string;
  preferred_language?: string;
  [key: string]: any;
}

export interface CreateGroupRequest {
  name: string;
  description?: string;
  organization_id: number;
  program_id?: number;
  filters: Filter[];
}

export interface UpdateGroupRequest {
  name?: string;
  description?: string;
  filters?: Filter[];
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const groupsApi = {
  /**
   * List all groups
   */
  async listGroups(params?: {
    organization_id?: number;
    program_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<Group[]> {
    try {
      const response = await apiClient.get('/api/groups', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get group by ID
   */
  async getGroup(groupId: string): Promise<Group> {
    try {
      const response = await apiClient.get(`/api/groups/${groupId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create new group
   */
  async createGroup(group: CreateGroupRequest): Promise<Group> {
    try {
      const response = await apiClient.post('/api/groups', group);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update group
   */
  async updateGroup(groupId: string, updates: UpdateGroupRequest): Promise<Group> {
    try {
      const response = await apiClient.put(`/api/groups/${groupId}`, updates);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete group
   */
  async deleteGroup(groupId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/groups/${groupId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get group members (with filters applied)
   */
  async getGroupMembers(groupId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<GroupMember[]> {
    try {
      const response = await apiClient.get(`/api/groups/${groupId}/members`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Freeze group membership
   */
  async freezeGroup(groupId: string): Promise<Group> {
    try {
      const response = await apiClient.post(`/api/groups/${groupId}/freeze`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Export group members to CSV
   */
  async exportGroupMembers(groupId: string): Promise<Blob> {
    try {
      const response = await apiClient.get(`/api/groups/${groupId}/export`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};
